import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import api from '../api/axios';
import './PublicTrackingView.css';
import StatusIcon from '../components/StatusIcon';
import { formatDuration } from '../utils/dateUtils';

const PublicTrackingView = () => {
    const { token } = useParams();
    const [instance, setInstance] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchInstance();
    }, [token]);

    const fetchInstance = async () => {
        try {
            const response = await api.get(`/public/tracking/${token}`);
            setInstance(response.data.data.instance);
        } catch (error) {
            console.error('Error fetching instance:', error);
            setInstance(null);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="public-tracking">
                <div className="container">
                    <div className="spinner-container">
                        <div className="spinner"></div>
                        <p>Ładowanie statusu...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!instance) {
        return (
            <div className="public-tracking">
                <SEO title="Nie znaleziono śledzenia" />
                <div className="container">
                    <div className="error-state card">
                        <div className="error-icon">⚠️</div>
                        <h2>Śledzenie nie zostało znalezione</h2>
                        <p className="text-muted">
                            Link może być nieprawidłowy lub śledzenie zostało usunięte.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="public-tracking">
            <SEO
                title={`Status: ${instance.currentStatus} - ${instance.templateSnapshot.name}`}
                description={`Sprawdź aktualny status dla: ${instance.templateSnapshot.name}. Obecny status: ${instance.currentStatus}`}
            />
            <div className="container container-sm">

                {/* Header */}
                <div className="header card">
                    {instance.logoUrl ? (
                        // Jeśli firma wgrała własne logo, pokazujemy TYLKO je (bez błyskawicy)
                        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                            <img
                                src={`/api/${instance.logoUrl}`}
                                alt="Logo Firmy"
                                style={{ maxHeight: '80px', maxWidth: '250px', objectFit: 'contain' }}
                            />
                        </div>
                    ) : (
                        // Jeśli nie ma własnego logo, pokazujemy logo serwisu (błyskawica)
                        <div className="app-logo" style={{ textAlign: 'center', marginBottom: '1rem' }}>
                            <svg width="40" height="40" fill="#F5DF4E" stroke="#F5DF4E" strokeWidth="2" viewBox="0 0 24 24">
                                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                            </svg>
                        </div>
                    )}
                    <h1>{instance.templateSnapshot.name}</h1>
                    {instance.templateSnapshot.description && (
                        <p className="text-muted">{instance.templateSnapshot.description}</p>
                    )}
                </div>

                {/* Current Status Badge */}
                <div className="current-status card">
                    <h3>Aktualny status</h3>
                    <div className="status-current">
                        {instance.templateSnapshot.statuses.find(s => s.name === instance.currentStatus) && (
                            <div className="status-badge-current">
                                <span className="status-icon">
                                    <StatusIcon iconId={instance.templateSnapshot.statuses.find(s => s.name === instance.currentStatus).icon} size={24} />
                                </span>
                                <span className="status-name">{instance.currentStatus}</span>
                            </div>
                        )}
                    </div>
                    <p className="text-muted status-time">
                        Ostatnia aktualizacja: {new Date(instance.updatedAt).toLocaleString('pl-PL')}
                    </p>
                </div>

                {/* Instance Info - Same as Update View */}
                {(() => {
                    const visibleFields = instance.fieldValues && Object.entries(instance.fieldValues)
                        .filter(([key]) => isNaN(key))
                        .filter(([key]) => {
                            const field = instance.templateSnapshot.customFields?.find(f => f.name === key);
                            return field?.showInPublicView === true;
                        });

                    if (!visibleFields || visibleFields.length === 0) return null;

                    return (
                        <div className="instance-info card">
                            <h3>Dane:</h3>
                            <div className="info-grid">
                                {visibleFields.map(([key, value]) => {
                                    const field = instance.templateSnapshot.customFields?.find(f => f.name === key);
                                    // Helper to format label if missing
                                    const formatLabel = (str) => {
                                        return str
                                            .replace(/([A-Z])/g, ' $1') // insert space before capital letters
                                            .replace(/^./, str => str.toUpperCase()) // uppercase the first character
                                            .trim();
                                    };

                                    const label = field?.label || formatLabel(key);

                                    return (
                                        <div key={key} className="info-item">
                                            <span className="info-label">{label}:</span>
                                            <span className="info-value">
                                                {typeof value === 'object' && value !== null
                                                    ? JSON.stringify(value)
                                                    : value?.toString() || '-'}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })()}

                {/* Timeline / History */}
                <div className="history-section card">
                    <h3>Historia przebiegu</h3>
                    {!instance.history || instance.history.length === 0 ? (
                        <div className="empty-state-sm">
                            <p className="text-muted">Brak historii statusów</p>
                        </div>
                    ) : (
                        <div className="history-timeline">
                            {instance.history.slice().reverse().map((entry, index) => { // Reverse to show latest first
                                return (
                                    <div key={index} className="timeline-item">
                                        <div className="timeline-marker"></div>
                                        <div className="timeline-content">
                                            <div className="timeline-header">
                                                <span className="timeline-status">{entry.status}</span>
                                                <span className="timeline-date text-muted">
                                                    {new Date(entry.timestamp).toLocaleString('pl-PL')}
                                                </span>
                                            </div>

                                            {/* Notes if present */}
                                            {entry.notes && (
                                                <p className="timeline-notes">{entry.notes}</p>
                                            )}

                                            {/* Photos if present */}
                                            {entry.photos && entry.photos.length > 0 && (
                                                <div className="timeline-photos">
                                                    {entry.photos.map((photo, pIdx) => {
                                                        // Handle photo URL - check if absolute or relative
                                                        const photoUrl = photo.startsWith('http') ? photo : `/${photo}`;
                                                        return (
                                                            <a
                                                                key={pIdx}
                                                                href={photoUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="photo-link"
                                                            >
                                                                <img
                                                                    src={photoUrl}
                                                                    alt={`Zdjęcie ${pIdx + 1}`}
                                                                    className="photo-thumbnail"
                                                                />
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="marketing-banner">
                    <p>Chcesz taki system powiadomień w swojej firmie?</p>
                    <a
                        href="https://sprawdzstatus.pl"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline"
                    >
                        Sprawdź jak to działa na SprawdzStatus.pl 🚀
                    </a>
                </div>

            </div>
        </div>
    );
};

export default PublicTrackingView;
